import {
  Autocomplete,
  AutocompleteOption,
  Button,
  Card,
  Divider,
  FormHelperText,
  Input,
  Stack,
  ToggleButtonGroup,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useBlocker } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  CaseManagementDto,
  CaseManagementDtoCaseConcluded,
  CaseManagementDtoComplexCaseStatus,
  CaseManagementDtoContactTracing,
  CaseManagementDtoContactTracingType,
  CaseManagementDtoDirectlyObservedTherapyNeeded,
  CaseManagementDtoDirectPayer,
  CaseManagementDtoTuberculosisFund,
  PatientDto,
  UserDto,
} from '../../../../api/generated/model';
import {
  getGetCaseManagementByPatientIdQueryKey,
  getGetPatientByIdQueryKey,
  getValidateCaseNumberQueryOptions,
  useGetCaseManagementByPatientIdSuspense,
  useUpdateCaseManagementByPatientId,
  useUpdatePatient,
} from '../../../../api/generated/patient-controller/patient-controller.ts';
import { useUsers } from '../../../../api/created/users.ts';
import { LeavePageWarning } from '../../../../components/LeavePageWarning.tsx';
import { SingeLineFormGrid, SingleLineFormControl } from '../../../../components/form/SingeLineFormComponents.tsx';
import { FieldMetaInfos, useAutoSaveForm } from '../../../../hooks/useAutoSaveForm.ts';
import { getCaseManagementStatusName, getComplexCaseStatusName } from '../../../../utils/caseManagement.ts';
import { getCaseNumberErrorMessage } from '../../../../utils/caseNumber.ts';
import { getStringOrUndefined } from '../../../../utils/string.ts';
import { CaseClosureChecklistDialog } from './CaseClosureChecklistDialog.tsx';

export function EditCaseManagementForm({ patient }: { patient: PatientDto }) {
  const patientId = patient.id;
  const queryClient = useQueryClient();
  const { data: caseManagement } = useGetCaseManagementByPatientIdSuspense(patientId);
  const { data: users = [], isPending: isUsersPending } = useUsers();

  const updateCaseManagementMutation = useUpdateCaseManagementByPatientId({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(getGetCaseManagementByPatientIdQueryKey(patientId), data);
      },
      onError: () =>
        toast.error('Beim speichern des Fallmanagement ist ein Fehler aufgetreten', { toastId: 'saveError' }),
    },
  });

  const updatePatientMutation = useUpdatePatient({
    mutation: {
      onSuccess: (data) => {
        queryClient.setQueryData(getGetPatientByIdQueryKey(patient.id), data);
      },
      onError: () => toast.error('Beim Speichern der Fallnummer ist ein Fehler aufgetreten', { toastId: 'saveError' }),
    },
  });

  const formSourceData = useMemo(() => ({ caseManagement, patient }), [caseManagement, patient]);

  const {
    control,
    register,
    getFieldState,
    formState: { errors, isFormDirty },
    watch,
    scheduleSave,
  } = useAutoSaveForm({
    sourceData: formSourceData,
    getDefaultValues,
    save: async (data) => {
      const caseManagementPayload: CaseManagementDto = {
        assignedEmployeeId: data.assignedEmployeeId,
        complexCaseStatus: data.complexCaseStatus,
        contactTracing: data.contactTracing,
        contactTracingType: data.contactTracingType,
        tuberculosisFund: data.tuberculosisFund,
        directPayer: data.directPayer,
        caseConcluded: data.caseConcluded,
        directlyObservedTherapyNeeded: data.directlyObservedTherapyNeeded,
      };
      const latestPatient = queryClient.getQueryData<PatientDto>(getGetPatientByIdQueryKey(patient.id)) ?? patient;

      const caseManagementPromise = hasCaseManagementChanges(data, caseManagement)
        ? updateCaseManagementMutation.mutateAsync({
            id: patientId,
            data: caseManagementPayload,
          })
        : Promise.resolve(caseManagement);

      const patientPromise =
        data.caseNumber !== (latestPatient.caseNumber ?? '')
          ? updatePatientMutation.mutateAsync({
              data: {
                ...latestPatient,
                caseNumber: getStringOrUndefined(data.caseNumber),
              },
            })
          : Promise.resolve(latestPatient);

      const [updatedCaseManagement, updatedPatient] = await Promise.all([caseManagementPromise, patientPromise]);

      return { caseManagement: updatedCaseManagement, patient: updatedPatient };
    },
    fieldMetaInfos,
    onValidationFailed: () =>
      toast.warn('Fallmanagement kann aufgrund eines Fehlers momentan nicht gespeichert werden', {
        toastId: 'validationError',
        autoClose: false,
      }),
    onValidationPassed: () => toast.dismiss('validationError'),
  });

  const caseConcludedAt = caseManagement.caseConcludedAt;

  const [checklistDialogOpen, setChecklistDialogOpen] = useState(false);
  const [pendingCaseConcludedChange, setPendingCaseConcludedChange] = useState<
    ((value: CaseManagementDtoCaseConcluded) => void) | null
  >(null);

  const contactTracingValue = watch('contactTracing');
  // The backend gates whether contact tracing may be marked as completed (e.g. all open tasks done).
  const isContactTracingCompletable = caseManagement.canCompleteContactTracing !== false;
  const shouldShowContactTracingField = contactTracingValue === CaseManagementDtoContactTracing.YES;

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) => {
        const left = getUserLabel(a);
        const right = getUserLabel(b);

        return left.localeCompare(right);
      }),
    [users],
  );

  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty && !updateCaseManagementMutation.isPending && !updatePatientMutation.isPending,
    withResolver: true,
  });

  return (
    <>
      <Card>
        <Typography level={'title-lg'}>Fallmanagement</Typography>
        <Divider />
        <SingeLineFormGrid>
          <SingleLineFormControl label="Fallnummer" fieldState={getFieldState('caseNumber')}>
            <Input
              {...register('caseNumber', {
                required: true,
                validate: {
                  duplicate: (caseNumber) =>
                    !caseNumber ||
                    queryClient
                      .fetchQuery(
                        getValidateCaseNumberQueryOptions(patient.id, { caseNumber }, { query: { staleTime: 60000 } }),
                      )
                      .then(({ isValid }) => isValid),
                },
              })}
              variant="plain"
            />
            {errors.caseNumber && <FormHelperText>{getCaseNumberErrorMessage(errors.caseNumber)}</FormHelperText>}
          </SingleLineFormControl>
          <Controller
            name={'assignedEmployeeId'}
            control={control}
            render={({ field, fieldState }) => (
              <SingleLineFormControl label={'Fallführende Mitarbeiter/in'} fieldState={fieldState}>
                <Autocomplete
                  value={sortedUsers.find((user) => user.id === field.value) ?? null}
                  options={sortedUsers}
                  loading={isUsersPending}
                  variant={'outlined'}
                  getOptionLabel={getUserLabel}
                  isOptionEqualToValue={(option, selectedValue) => option.id === selectedValue.id}
                  onChange={(_, selectedValue) => field.onChange(selectedValue?.id ?? '')}
                  renderOption={(props, option) => (
                    <AutocompleteOption {...props} key={option.id}>
                      {getUserLabel(option)}
                    </AutocompleteOption>
                  )}
                />
              </SingleLineFormControl>
            )}
          />
          <SingleLineFormControl label={'Komplexer Fall'} fieldState={getFieldState('complexCaseStatus')}>
            <Controller
              name={'complexCaseStatus'}
              control={control}
              render={({ field: { value, onChange } }) => (
                <ToggleButtonGroup value={value} spacing={1}>
                  {Object.values(CaseManagementDtoComplexCaseStatus).map((status) => (
                    <Button key={status} value={status} onClick={() => onChange(status)}>
                      {getComplexCaseStatusName(status)}
                    </Button>
                  ))}
                </ToggleButtonGroup>
              )}
            />
          </SingleLineFormControl>
          <SingleLineFormControl
            label={
              <>
                Umgebungs
                <br />
                untersuchung
              </>
            }
            labelSx={{ py: 0, lineHeight: '21px' }}
            fieldState={getFieldState('contactTracing')}>
            <Controller
              name={'contactTracing'}
              control={control}
              render={({ field: { value, onChange } }) => (
                <CaseManagementStatusButtonGroup
                  value={value}
                  onChange={onChange}
                  type="contactTracing"
                  disabledValues={
                    isContactTracingCompletable ? undefined : [CaseManagementDtoContactTracing.COMPLETED]
                  }
                />
              )}
            />
          </SingleLineFormControl>

          <SingleLineFormControl
            label={'Art der Umgebungs untersuchung'}
            fieldState={getFieldState('contactTracingType')}>
            <Controller
              name={'contactTracingType'}
              control={control}
              render={({ field: { value, onChange } }) => (
                <CaseManagementStatusButtonGroup
                  disabled={!shouldShowContactTracingField}
                  value={value as CaseManagementDtoContactTracingType}
                  onChange={onChange}
                  type="contactTracingType"
                />
              )}
            />
          </SingleLineFormControl>

          <SingleLineFormControl label={'TB Fonds'} fieldState={getFieldState('tuberculosisFund')}>
            <Controller
              name={'tuberculosisFund'}
              control={control}
              render={({ field: { value, onChange } }) => (
                <CaseManagementStatusButtonGroup value={value} onChange={onChange} />
              )}
            />
          </SingleLineFormControl>
          <SingleLineFormControl label={'Selbstzahler'} fieldState={getFieldState('directPayer')}>
            <Controller
              name={'directPayer'}
              control={control}
              render={({ field: { value, onChange } }) => (
                <CaseManagementStatusButtonGroup value={value} onChange={onChange} />
              )}
            />
          </SingleLineFormControl>
          <SingleLineFormControl label={'DOT notwendig'} fieldState={getFieldState('directlyObservedTherapyNeeded')}>
            <Controller
              name={'directlyObservedTherapyNeeded'}
              control={control}
              render={({ field: { value, onChange } }) => (
                <CaseManagementStatusButtonGroup value={value} onChange={onChange} />
              )}
            />
          </SingleLineFormControl>
        </SingeLineFormGrid>
      </Card>

      <Card>
        <SingeLineFormGrid>
          <SingleLineFormControl label={'Fall abgeschlossen'} fieldState={getFieldState('caseConcluded')}>
            <Stack direction={'row'} alignItems={'center'} gap={2}>
              <Controller
                name={'caseConcluded'}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <ToggleButtonGroup value={value} spacing={1}>
                    {[CaseManagementDtoCaseConcluded.YES, CaseManagementDtoCaseConcluded.NO].map((status) => (
                      <Button
                        key={status}
                        value={status}
                        onClick={() => {
                          if (status === CaseManagementDtoCaseConcluded.YES) {
                            if (value === CaseManagementDtoCaseConcluded.YES && caseConcludedAt != null) {
                              return;
                            }
                            setPendingCaseConcludedChange(() => onChange);
                            setChecklistDialogOpen(true);
                            return;
                          }
                          onChange(status);
                        }}>
                        {getCaseManagementStatusName(status)}
                      </Button>
                    ))}
                  </ToggleButtonGroup>
                )}
              />
              {caseConcludedAt != null && (
                <Typography level={'body-md'}>{caseConcludedAt.split('-').reverse().join('.')}</Typography>
              )}
            </Stack>
          </SingleLineFormControl>
        </SingeLineFormGrid>
      </Card>

      <LeavePageWarning
        open={blocker.status === 'blocked'}
        onDelete={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
        onSave={() => {
          blocker.reset?.();
          scheduleSave();
        }}
        title={'Daten von Fallmanagement behalten?'}
      />
      <CaseClosureChecklistDialog
        patientId={patientId}
        open={checklistDialogOpen}
        onClose={() => {
          setChecklistDialogOpen(false);
          setPendingCaseConcludedChange(null);
        }}
        onConfirm={() => {
          pendingCaseConcludedChange?.(CaseManagementDtoCaseConcluded.YES);
          setChecklistDialogOpen(false);
          setPendingCaseConcludedChange(null);
        }}
      />
    </>
  );
}

type CaseManagementStatus =
  | CaseManagementDtoContactTracing
  | CaseManagementDtoContactTracingType
  | CaseManagementDtoCaseConcluded;

type CaseManagementStatusButtonGroupType = 'contactTracing' | 'contactTracingType';

function CaseManagementStatusButtonGroup({
  value,
  onChange,
  disabled = false,
  disabledValues,
  type,
}: {
  value: CaseManagementStatus;
  disabled?: boolean;
  disabledValues?: CaseManagementStatus[];
  onChange: (status: CaseManagementStatus) => void;
  type?: CaseManagementStatusButtonGroupType;
}) {
  const options =
    type === 'contactTracing'
      ? CaseManagementDtoContactTracing
      : type === 'contactTracingType'
        ? CaseManagementDtoContactTracingType
        : CaseManagementDtoCaseConcluded;

  return (
    <ToggleButtonGroup value={value} spacing={1} disabled={disabled}>
      {Object.values(options).map((status) => (
        <Button
          key={status}
          value={status}
          disabled={disabledValues?.includes(status)}
          onClick={() => onChange(status)}>
          {getCaseManagementStatusName(status)}
        </Button>
      ))}
    </ToggleButtonGroup>
  );
}

function getUserLabel(user: UserDto) {
  const name = `${user.lastName ?? ''} ${user.firstName ?? ''}`.trim();
  return name || user.username || user.email || user.id;
}

interface FormValues {
  caseNumber: string;
  complexCaseStatus: CaseManagementDtoComplexCaseStatus;
  contactTracing: CaseManagementDtoContactTracing;
  contactTracingType?: CaseManagementDtoContactTracingType;
  tuberculosisFund: CaseManagementDtoTuberculosisFund;
  directPayer: CaseManagementDtoDirectPayer;
  caseConcluded: CaseManagementDtoCaseConcluded;
  directlyObservedTherapyNeeded: CaseManagementDtoDirectlyObservedTherapyNeeded;
  assignedEmployeeId: string;
}

interface FormSourceData {
  caseManagement: CaseManagementDto;
  patient: PatientDto;
}

function getDefaultValues({ caseManagement, patient }: FormSourceData): FormValues {
  return {
    caseNumber: patient.caseNumber ?? '',
    assignedEmployeeId: caseManagement.assignedEmployeeId ?? '',
    complexCaseStatus: caseManagement.complexCaseStatus,
    contactTracing: caseManagement.contactTracing,
    contactTracingType: caseManagement.contactTracingType,
    tuberculosisFund: caseManagement.tuberculosisFund,
    directPayer: caseManagement.directPayer,
    caseConcluded: caseManagement.caseConcluded,
    directlyObservedTherapyNeeded: caseManagement.directlyObservedTherapyNeeded,
  };
}

const fieldMetaInfos: FieldMetaInfos<FormValues> = {
  caseNumber: { debounce: true },
  assignedEmployeeId: { debounce: false },
  complexCaseStatus: { debounce: false },
  contactTracing: { debounce: false },
  contactTracingType: { debounce: false },
  tuberculosisFund: { debounce: false },
  directPayer: { debounce: false },
  caseConcluded: { debounce: false },
  directlyObservedTherapyNeeded: { debounce: false },
};

function hasCaseManagementChanges(data: FormValues, caseManagement: CaseManagementDto) {
  return (
    data.assignedEmployeeId !== (caseManagement.assignedEmployeeId ?? '') ||
    data.complexCaseStatus !== caseManagement.complexCaseStatus ||
    data.contactTracing !== caseManagement.contactTracing ||
    data.contactTracingType !== caseManagement.contactTracingType ||
    data.tuberculosisFund !== caseManagement.tuberculosisFund ||
    data.directPayer !== caseManagement.directPayer ||
    data.caseConcluded !== caseManagement.caseConcluded ||
    data.directlyObservedTherapyNeeded !== caseManagement.directlyObservedTherapyNeeded
  );
}
